import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Wallet as WalletIcon, Loader2, Coins, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, inr2 } from "../../lib/api";
import { FullPageLoader } from "../../components/Loaders";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";

// Customer Wallet — balance + transactions + UPI withdrawal request.
export default function WalletPage() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const [data, setData] = useState(null);
    const [withdrawals, setWithdrawals] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ amount: "", upi_id: "" });

    const reload = async () => {
        try {
            const { data } = await api.get("/wallet");
            setData(data);
        } catch { setData({ wallet: null, transactions: [] }); }
        try {
            const { data } = await api.get("/wallet/withdrawals");
            setWithdrawals(data || []);
        } catch { setWithdrawals([]); }
    };
    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await reload(); })();
        return () => { cancelled = true; };
    }, []);

    const submit = async () => {
        const amt = parseFloat(form.amount);
        if (!amt || amt < 100) { toast.error("Minimum withdrawal is ₹100"); return; }
        if (!form.upi_id.includes("@")) { toast.error("Enter a valid UPI ID"); return; }
        setBusy(true);
        try {
            await api.post("/wallet/withdraw", { amount: amt, upi_id: form.upi_id.trim() });
            toast.success("Withdrawal requested");
            setOpen(false); setForm({ amount: "", upi_id: "" });
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
        finally { setBusy(false); }
    };

    const cancelWd = async (wid) => {
        if (!window.confirm("Cancel this pending withdrawal? The amount goes back to your wallet.")) return;
        try {
            await api.post(`/wallet/withdrawals/${wid}/cancel`);
            toast.success("Withdrawal cancelled");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    if (!data) return <FullPageLoader />;
    const balance = data.wallet?.balance || 0;

    return (
        <div className="pb-32">
            {blinkit ? (
                <BkPageHeader title="Wallet" back="/profile" />
            ) : (
                <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card border-b border-border">
                    <Link to="/profile" className="p-1 -ml-1"><ArrowLeft size={22} /></Link>
                    <h1 className="text-base font-bold">Wallet</h1>
                </header>
            )}

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:items-start">
            <div className="min-w-0">
            <section className="m-4 bg-gradient-to-br from-primary to-primary/70 text-primary-foreground rounded-3xl p-6">
                <p className="text-xs uppercase tracking-[0.2em] opacity-80 font-bold">Available balance</p>
                <p className="text-4xl font-display font-bold mt-2" data-testid="wallet-balance">{inr2(balance)}</p>
                <p className="text-xs opacity-80 mt-2">{data.wallet?.coins || 0} coins</p>
                <button
                    onClick={() => setOpen(true)}
                    disabled={balance < 100}
                    className="mt-4 inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl text-xs font-bold backdrop-blur"
                    data-testid="wallet-withdraw-btn"
                ><WalletIcon size={13} /> Withdraw to UPI</button>
                {balance < 100 && <p className="text-[10px] opacity-70 mt-2">Minimum balance ₹100 required for withdrawal.</p>}
            </section>

            {withdrawals.length > 0 && (
                <section className="m-4" data-testid="wallet-withdrawals-list">
                    <h3 className="font-bold mb-3">Withdrawals</h3>
                    <div className="bg-card rounded-2xl border border-border divide-y divide-border">
                        {withdrawals.map((w) => (
                            <div key={w.id} className="px-4 py-3 flex items-center gap-3" data-testid={`wd-${w.id}`}>
                                <div className="flex-1 text-sm">
                                    <p className="font-semibold">{inr2(w.amount)} → <span className="font-mono text-xs">{w.upi_id}</span></p>
                                    <p className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString()}</p>
                                    {w.txn_ref && <p className="text-[10px] text-emerald-600 font-mono mt-0.5">UTR: {w.txn_ref}</p>}
                                    {w.reject_reason && <p className="text-[10px] text-red-600 mt-0.5">{w.reject_reason}</p>}
                                </div>
                                <Badge status={w.status} />
                                {w.status === "PENDING" && (
                                    <button onClick={() => cancelWd(w.id)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`wd-cancel-${w.id}`}>Cancel</button>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* iter101 — dedicated Coins card linking to the enriched
                /coins page. The wallet balance and coin balance are
                separate ledgers — the customer earns coins on orders
                and can convert them to wallet cash at the tenant's
                configured rate. */}
            <Link
                to="/coins"
                data-testid="wallet-coins-card"
                className="mx-4 mb-4 bg-gradient-to-br from-amber-400 to-orange-500 text-white rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md active:scale-[0.99] transition"
            >
                <div className="w-11 h-11 rounded-xl bg-white/20 inline-flex items-center justify-center flex-shrink-0">
                    <Coins size={22} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-90">My coins</p>
                    <p className="text-xl font-extrabold leading-tight">{data.wallet?.coins || 0} <span className="text-xs font-semibold opacity-80">coins</span></p>
                    <p className="text-[11px] opacity-90">See earning history · convert to wallet</p>
                </div>
                <ChevronRight size={18} className="opacity-80" />
            </Link>
            </div>
            <div className="min-w-0">
            <section className="m-4">
                <h3 className="font-bold mb-3">Transactions</h3>
                <div className="bg-card rounded-2xl border border-border divide-y divide-border">
                    {data.transactions.length === 0 ? (
                        <p className="text-center text-sm text-muted-foreground py-10">No transactions yet</p>
                    ) : data.transactions.map((tx) => {
                        const isCredit = tx.delta > 0;
                        return (
                            <div key={tx.id} className="flex items-center gap-3 px-4 py-3" data-testid={`txn-${tx.id}`}>
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                    {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                                </div>
                                <div className="flex-1 text-sm">
                                    <p className="font-semibold">{tx.reason || tx.kind}</p>
                                    <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString()} · {tx.kind}</p>
                                </div>
                                <div className={`text-sm font-bold ${isCredit ? "text-emerald-600" : "text-red-600"}`}>{isCredit ? "+" : "-"}{inr2(Math.abs(tx.delta))}</div>
                            </div>
                        );
                    })}
                </div>
            </section>
            </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
                    <div className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="wd-modal">
                        <h2 className="text-lg font-bold mb-1">Withdraw to UPI</h2>
                        <p className="text-xs text-muted-foreground mb-4">Available: <b>{inr2(balance)}</b>. Tenant processes manually within 24 hours.</p>
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Amount (₹)</label>
                        <input
                            type="number"
                            inputMode="decimal"
                            min="100"
                            max={balance}
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            placeholder="100"
                            className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-primary/30"
                            data-testid="wd-amount"
                        />
                        <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mt-3 block">UPI ID</label>
                        <input
                            value={form.upi_id}
                            onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                            placeholder="yourname@bank"
                            className="mt-1 w-full bg-muted rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 ring-primary/30"
                            data-testid="wd-upi"
                        />
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => setOpen(false)} className="flex-1 rounded-xl bg-muted text-foreground py-2.5 text-sm font-semibold" data-testid="wd-cancel">Cancel</button>
                            <button onClick={submit} disabled={busy} className="flex-1 rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="wd-submit">{busy && <Loader2 size={13} className="animate-spin" />}Submit</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Badge({ status }) {
    const m = {
        PENDING: { color: "bg-amber-100 text-amber-700" },
        PAID: { color: "bg-emerald-100 text-emerald-700" },
        REJECTED: { color: "bg-red-100 text-red-700" },
    }[status] || { color: "bg-muted text-foreground" };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${m.color}`}>{status}</span>;
}
